// Prévia de link — a entrada do cartão sem tirar a pessoa do lugar (desenho §3, spec §3.5).
//
// Quando o cartão do site chega (ou muda de altura: imagem que falha, fonte que chega), a lista que a pessoa
// está lendo cresce ou encolhe. Nenhuma das quatro listas (chat, atendimento da equipe, portal, feed) trata isso,
// e o `overflow-anchor` do navegador cobre só uma parte. Aqui fica a decisão pura, sem tocar o DOM; o hook
// `useEntradaSemPulo` mede e aplica.
//
// 🔴 Desde o T-R19 (28/10) as listas de conversa cuidam disso sozinhas (`lib/rolagem/grudadoNoPe`, `useGrudadoNoPe`: o
// pé e, onde o navegador não ancora, a âncora própria): ali o cartão não decide nada. Estas regras ficam para quem não
// se gruda (o fio, o feed).
import { FOLGA_DO_PE_PX, navegadorAncoraRolagem } from "@/lib/rolagem/grudadoNoPe";

/**
 * Até quantos pixels do fim da lista ainda contam como "estava no pé" (o mesmo da lista grudada); e se o navegador
 * ancora a rolagem (a lista grudada usa a mesma resposta para a âncora própria).
 */
export { FOLGA_DO_PE_PX, navegadorAncoraRolagem };

export interface MedidaDaEntrada {
  /** Quanto o cartão cresceu (px). Com sinal: encolher (a imagem falhou) é negativo. */
  delta: number;
  /** Medidas do rolável DEPOIS da mudança, que é como o navegador as entrega. */
  scrollTop: number;
  scrollHeight: number;
  clientHeight: number;
  topoDoCartao: number;
  /** Topo da área visível do rolável; 0 quando quem rola é a janela. */
  topoDoRolavel: number;
  /** O navegador já compensa conteúdo que cresce acima do que se lê (Chrome, Firefox). */
  navegadorAncora: boolean;
  /**
   * A regra 1 vale para esta medida? Ausente ou `true`: sim. `false` quando a lista se gruda no pé sozinha
   * (`useGrudadoNoPe`: o pé é dela) e quando o cartão cresceu ACIMA do topo visível (com a âncora do navegador a
   * distância ao pé não mudou, e a conta do delta puxaria ao pé quem lê a menos de um cartão dele). As regras 2 e 3 não
   * mudam.
   */
  regraDoPe?: boolean;
}

/**
 * Decide para onde levar o `scrollTop`, ou `null` se nada deve mexer.
 *
 * 1. A lista estava no pé antes do cartão (a distância do pé sem o crescimento deste cartão ≤ 8): vai ao fim, e quem lê o
 *    fim continua no fim com o cartão à vista. Só com `regraDoPe` ausente ou `true`.
 * 2. O topo do cartão está acima da área visível e o navegador NÃO ancora: soma o `delta`, e o que a pessoa lê
 *    não se mexe. Onde o navegador ancora, a regra 2 não roda: ele já compensou e somar de novo faria a lista
 *    pular (o desenho §3 escreve `bottom − delta`; o certo é o TOPO do cartão, plano de 27/10, item 4).
 * 3. Senão (o cartão está na área visível ou abaixo dela), nada: ele aparece embaixo do que a pessoa lê.
 *
 * O resultado é um `scrollTop` absoluto; o navegador o prende ao intervalo da lista.
 */
export function decidirAjuste(m: MedidaDaEntrada): { scrollTop: number } | null {
  if (!Number.isFinite(m.delta) || m.delta === 0) return null;

  const distanciaDoPe = m.scrollHeight - m.delta - m.scrollTop - m.clientHeight;
  if (m.regraDoPe !== false && distanciaDoPe <= FOLGA_DO_PE_PX)
    return { scrollTop: m.scrollHeight };

  if (!m.navegadorAncora && m.topoDoCartao <= m.topoDoRolavel) {
    return { scrollTop: m.scrollTop + m.delta };
  }

  return null;
}

/**
 * O primeiro ancestral que rola na vertical (`overflow-y` `auto` ou `scroll`: o `div.flex-1` no fio, no atendimento e
 * no portal; o viewport de um `ScrollArea`). `null` = quem rola é a janela (o feed): `body` e `html` com
 * `overflow` próprio não contam, porque a janela se mede pelo `document.scrollingElement`.
 *
 * 🔴 O viewport do Radix (`[data-radix-scroll-area-viewport]`) conta pelo atributo, não pelo estilo: ele nasce com
 * `overflow-y: hidden` inline e só vira `scroll` depois de um efeito do Radix, e o cartão que monta no 1º commit (já no
 * cache) acharia a JANELA (medido no app em 28/10: escritas no `<html>`). Todo `ScrollArea` do app é vertical.
 */
export function rolavelDe(no: HTMLElement): HTMLElement | null {
  let atual = no.parentElement;
  while (atual && atual !== document.body && atual !== document.documentElement) {
    if (atual.hasAttribute("data-radix-scroll-area-viewport")) return atual;
    const { overflowY } = getComputedStyle(atual);
    if (overflowY === "auto" || overflowY === "scroll") return atual;
    atual = atual.parentElement;
  }
  return null;
}
